import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EditAccountModal = ({ account, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    group: '',
    balance: 0,
    currency: 'BBD',
    creditLimit: null,
    principalAmount: null,
    startDate: null,
    loanTerm: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (account) {
      setFormData({
        name: account?.name || '',
        type: account?.type || '',
        group: account?.group || '',
        balance: account?.balance || 0,
        currency: account?.currency || 'BBD',
        creditLimit: account?.creditLimit || null,
        principalAmount: account?.principalAmount || null,
        startDate: account?.startDate || null,
        loanTerm: account?.loanTerm || null
      });
    }
  }, [account]);

  const accountTypeOptions = [
    { value: 'Chequing', label: 'Chequing' },
    { value: 'Savings', label: 'Savings' },
    { value: 'Credit Card', label: 'Credit Card' },
    { value: 'Line of Credit', label: 'Line of Credit' },
    { value: 'Cash', label: 'Cash' },
    { value: 'Investment', label: 'Investment' },
    { value: 'Loan', label: 'Loan' }
  ];

  const accountGroupOptions = [
    { value: 'Assets', label: 'Assets' },
    { value: 'Liabilities', label: 'Liabilities' },
    { value: 'Cash', label: 'Cash' }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
    { value: 'AUD', label: 'AUD - Australian Dollar' },
    { value: 'JPY', label: 'JPY - Japanese Yen' },
    { value: 'INR', label: 'INR - Indian Rupee' },
    { value: 'BBD', label: 'BBD - Barbadian Dollar' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.name?.trim()) {
      setError('Account name is required');
      return;
    }

    if (!formData?.type) {
      setError('Account type is required');
      return;
    }

    if (!formData?.group) {
      setError('Account group is required');
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to update account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-16 bg-black/50">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-[500px] max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-20 md:p-24 flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-card-foreground">Edit Account</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-20 md:p-24">
          {error && (
            <div className="mb-20 p-16 bg-error/10 border border-error/20 rounded-lg flex items-start gap-12">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <p className="text-sm text-error flex-1">{error}</p>
            </div>
          )}

          <div className="space-y-20">
            <Input
              label="Account Name"
              placeholder="e.g., Primary Checking"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              required
            />

            <Select
              label="Account Type"
              options={accountTypeOptions}
              value={formData?.type}
              onChange={(e) => setFormData({ ...formData, type: e?.target?.value })}
              required
            />

            <Select
              label="Account Group"
              options={accountGroupOptions}
              value={formData?.group}
              onChange={(e) => setFormData({ ...formData, group: e?.target?.value })}
              required
            />

            <Input
              label="Current Balance"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData?.balance}
              onChange={(e) => setFormData({ ...formData, balance: parseFloat(e?.target?.value) || 0 })}
              required
            />

            {(formData?.type === 'Line of Credit' || formData?.type === 'Credit Card') && (
              <Input
                label="Credit Limit (Optional)"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData?.creditLimit || ''}
                onChange={(e) => setFormData({ ...formData, creditLimit: parseFloat(e?.target?.value) || null })}
                description="Enter the maximum credit limit for this account"
              />
            )}

            {formData?.type === 'Loan' && (
              <>
                <Input
                  label="Principal Amount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData?.principalAmount || ''}
                  onChange={(e) => setFormData({ ...formData, principalAmount: parseFloat(e?.target?.value) || null })}
                  description="Original loan amount"
                />
                <Input
                  label="Start Date"
                  type="date"
                  value={formData?.startDate || ''}
                  onChange={(e) => setFormData({ ...formData, startDate: e?.target?.value })}
                  description="Date when the loan started"
                />
                <Input
                  label="Loan Term (Months)"
                  type="number"
                  placeholder="e.g., 60 for 5 years"
                  value={formData?.loanTerm || ''}
                  onChange={(e) => setFormData({ ...formData, loanTerm: parseInt(e?.target?.value) || null })}
                  description="Total loan duration in months"
                />
              </>
            )}

            <Select
              label="Currency"
              options={currencyOptions}
              value={formData?.currency}
              onChange={(e) => setFormData({ ...formData, currency: e?.target?.value })}
              required
            />
          </div>

          <div className="flex gap-12 mt-24">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={loading}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAccountModal;